import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserById } from "../../hooks/user/useUserById";
import Loading from "../../components/common/Loading";
import VerificationBadge from "../user/VerificationBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons";

const LeagueMemberDetail = ({ memberId, totalScore, placement, user: userProp }) => {
  const navigate = useNavigate();
  // Prefer the user already embedded in the list payload (leaderboard rows ship
  // member.user via the backend $lookup). Only fall back to a per-row fetch when
  // no user object was passed, which avoids an N+1 of /users/:id calls.
  const { data: fetchedUser, isLoading, error } = useUserById(
    userProp ? undefined : memberId,
  );
  const user = userProp || fetchedUser;

  const displayName = user
    ? user.username || user.name || "Unknown User"
    : "";

  if (!userProp && isLoading)
    return (
      <div className="flex items-center justify-center py-2">
        <Loading fullScreen={false} />
      </div>
    );

  if (!userProp && error)
    return (
      <div className="text-deep-red text-sm font-medium">
        Error loading user
      </div>
    );

  // Format score for better readability
  const formattedScore = totalScore
    ? parseFloat(totalScore).toFixed(2)
    : "0.00";

  // Add special styling for top 3 placements
  const isTopThree = placement <= 3;

  // Handle click navigation to user profile
  const handleUserClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (user?._id) {
      navigate(`/users/${user._id}`);
    }
  };

  return (
    <div className="flex items-center justify-between w-full gap-3">
      {/* User Info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCircleUser}
                className="text-medium-grey w-5 h-5"
              />
            </div>
          )}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={handleUserClick}
              className={`font-semibold text-sm truncate hover:underline cursor-pointer text-left ${
                isTopThree
                  ? "text-dark-grey hover:text-deep-red"
                  : "text-medium-grey hover:text-dark-grey"
              }`}
              title={`View ${displayName}'s profile`}
            >
              {displayName}
            </button>
            {user?.verified && (
              <VerificationBadge
                verified={user.verified}
                size="small"
                showTooltip={true}
              />
            )}
          </div>
          {isTopThree && (
            <div className="text-xs text-medium-grey">
              {placement === 1
                ? "Champion"
                : placement === 2
                  ? "Runner-up"
                  : "3rd Place"}
            </div>
          )}
        </div>
      </div>

      {/* Score */}
      <div className="flex-shrink-0">
        <div
          className={`text-right ${
            placement === 1
              ? "text-gold"
              : placement === 2
                ? "text-gray-500"
                : placement === 3
                  ? "text-amber-700"
                  : "text-dark-grey"
          }`}
        >
          <div className="font-bold text-base">{formattedScore}</div>
          <div className="text-xs text-medium-grey">points</div>
        </div>
      </div>
    </div>
  );
};

export default LeagueMemberDetail;
